/**
 * Tutorial feature module — the guided tours' bridge surface.
 * Inbound (Unity→web): startTutorial (arm the rail after the tutorial project push completes, and
 * open the Script tab on the first scene — 20260907; since 20260921 it also arms the CHARACTERS
 * tour, which opens on Boards instead), exitTutorial (Unity-side exits, e.g. Skip tour pressed in
 * the Character Creator's own WebView) and castMemberAdded (the creator handed a new cast member
 * back — NOT tour-gated, see its handler).
 * Outbound (web→Unity): tutorialEnded {reason}, tutorialBringOwnScript {}, tutorialNextTour {kind}.
 */
import { enqueueSendToNative } from '../transport';
import type { HandlerMap } from '../dispatch';
import { useTutorialStore, selectCharactersTourActive } from '@/store/tutorialStore';
import { useProjectStore } from '@/store/projectStore';
// Static import is safe HERE and nowhere else on this path: uiStore imports tutorialStore, which
// reaches this module through a DYNAMIC import — so uiStore → tutorialStore ⇢ tutorial.ts → uiStore
// is not a static cycle. Do NOT import uiStore statically from tutorialStore.
import { useUiStore } from '@/store/uiStore';
// A leaf module: tutorialConfig imports three image URLs and nothing else, so it closes no cycle.
import { resolveCharactersTourCastName } from '@/components/Tutorial/tutorialConfig';

/** `{}` for the Getting started tour; `{kind:'characters'}` for the Characters tour (design §2.5).
 *  The field is OPTIONAL and typed narrowly: an absent (or unrecognised) kind is today's tour, so a
 *  Unity build older than the characters work keeps working byte-for-byte. */
export interface StartTutorialMessage {
  type: 'startTutorial';
  payload?: { kind?: 'characters' };
}

/** Unity ending a tour from ITS side (design §2.5). Today's one sender is the Character Creator's
 *  Skip tour: the creator's WebView cancels its session, Unity shows the board again and then tells
 *  the board web the tour is over. `reason` is typed as unknown and normalised in the handler — it
 *  arrives off the wire. */
export interface ExitTutorialMessage {
  type: 'exitTutorial';
  payload?: { reason?: unknown };
}

/** A cast member the Avatar Customizer just baked, sent after Unity's own repush (design §2.5).
 *  Both fields come off the wire untyped and are normalised in the handler. */
export interface CastMemberAddedMessage {
  type: 'castMemberAdded';
  payload?: { name?: unknown; characterId?: unknown };
}

export type TutorialMessage = StartTutorialMessage | ExitTutorialMessage | CastMemberAddedMessage;

export function sendTutorialEnded(reason: 'skipped' | 'finished'): void {
  enqueueSendToNative('tutorialEnded', { reason });
}

export function sendTutorialBringOwnScript(): void {
  enqueueSendToNative('tutorialBringOwnScript', {});
}

/**
 * 20260921 (owner, tweak 6) — the finale's "Next: Learn about casting".
 *
 * Close THIS tour's project (Unity's own close, the same one tutorialBringOwnScript takes) and
 * start the next tour on the far side of it. The Casting project cannot be minted while a project
 * is open — Unity deletes the tutorial copy the user is standing in — so the close is not an
 * afterthought here, it is the mechanism.
 *
 * `kind` is the wire token ApplicationConstants.ApplicationTutorialKindCharacters, carried rather
 * than baked into the verb so the Locations tour costs nothing later. Unity's board dispatch is
 * exact-equality on the verb, so there is no prefix collision with tutorialEnded /
 * tutorialBringOwnScript.
 */
export function sendTutorialNextTour(kind: 'characters'): void {
  enqueueSendToNative('tutorialNextTour', { kind });
}

/**
 * The ONE exit the tour offers on every panel: "Skip tour" on the intro / render / coach cards and
 * "End tour" on the finale. Both end the tour AND leave the tutorial project — the owner's ruling
 * (20260904) is that a tour exit lands the user back on the Projects home, not inside the example
 * project with the card gone.
 *
 * `closePanel` is the same verb the shell's Home button and the hamburger's Close Project send
 * (SceneBoardPage.closeProject): Unity closes the project and lands on the Projects home. The
 * native tutorial flag is re-armed from the open context on EVERY open, so the order of
 * `tutorialEnded` (sent by the store's own `end`) vs `closePanel` is irrelevant.
 *
 * Beware the store↔bridge import cycle: tutorialStore.ts reaches this module through a DYNAMIC
 * import for exactly that reason, while this module imports the store statically. Keep it that way
 * — a new static import from the store side would close the cycle.
 */
export function exitTutorialToProjects(reason: 'skipped' | 'finished'): void {
  useTutorialStore.getState().end(reason);
  enqueueSendToNative('closePanel', {});
}

/**
 * 20260907 (owner): the tour opens on the SCRIPT tab, not the board. Unity is unchanged — it still
 * boots to the scene board and pushes `startTutorial`; the web flips the route.
 *
 * The order below is the whole of it, and it is load-bearing:
 *   1. arm the store (step 'script'),
 *   2. pin the tutorial scene,
 *   3. select that scene's FIRST shot — BEFORE the route flips. ScreenplayManager seeds its
 *      expanded scene from the selected shot in a mount-time `useState` initializer
 *      (ScreenplayManager.tsx ~L187) and CLEARS it when nothing is selected, so a selection made
 *      after the route change would arrive one render too late. `userInitiated` stays false: a
 *      non-user selection must not pop the shot panel on the eventual Boards arrival.
 *   4. flip the route.
 * Every step is guarded for a missing project — Unity can deliver this before any project load.
 */
export const tutorialHandlers = {
  startTutorial: (msg: StartTutorialMessage) => {
    // ── The CHARACTERS tour (20260921, design §2.3 / §2.5) ────────────────────────────────────
    // A different script on the same rail, so it arms differently: it STAYS on Boards (its own
    // panel 1 is what asks the user to tap Breakdown, and a route flip would skip that beat), and it
    // opens the cast card panel 3 describes. It pins the tutorial scene exactly as the arm below
    // does — same reason, the project the tour runs in is the one Unity just pushed — but selects
    // no shot, because nothing in this tour reads a selection.
    if (msg.payload?.kind === 'characters') {
      useTutorialStore.getState().start('characters');
      const scene = useProjectStore.getState().project?.scenes[0];
      if (scene) useTutorialStore.getState().setSceneId(scene.id);
      // BEST EFFORT, not the only try: Unity can push this before the project load resolves, in
      // which case there is no roster yet to match against. CharactersRail re-asserts it once one
      // exists, the way the example rail re-asserts its expanded scene.
      const castName = resolveCharactersTourCastName(
        useProjectStore.getState().project?.screenplayCharacters,
      );
      if (castName) useUiStore.getState().setScreenplayExpandedCharacter(castName);
      return;
    }
    useTutorialStore.getState().start();
    // The tutorial project has one scene; written generally anyway. `scenes` is already in
    // document order (the loader's), which is the same order the Script tab lists them in.
    const scene = useProjectStore.getState().project?.scenes[0];
    if (!scene) return;
    useTutorialStore.getState().setSceneId(scene.id);
    const firstShot = [...scene.shots].sort((a, b) => a.order - b.order)[0];
    if (firstShot) useProjectStore.getState().selectShot(firstShot.id, false);
    useUiStore.getState().setWorkspaceRoute('screenplay');
  },

  /**
   * Unity ending the tour (design §2.5). The web runs its OWN exit — the same one Skip tour and End
   * tour take — so the project close, the `tutorialEnded` report and the store teardown are one
   * path with one owner, however the exit was started.
   *
   * A malformed reason defaults to 'skipped', the conservative reading: the reason only rides
   * `tutorialEnded` and a run that did not demonstrably finish is not a completion.
   */
  exitTutorial: (msg: ExitTutorialMessage) => {
    exitTutorialToProjects(msg.payload?.reason === 'finished' ? 'finished' : 'skipped');
  },

  /**
   * A cast member came back from the Avatar Customizer.
   *
   * NOT TOUR-GATED (owner, 20260921): "castMemberAdded opens the new cast card EVERYWHERE (tour or
   * not)". Unity sends it after every creator save that added a cast row, and the landing below —
   * Cast sub-tab, that member's card expanded, scrolled into view — is the answer to "where did the
   * character I just made go?", which is a question an ordinary user asks just as loudly as a
   * tutorial one. Only the last three lines belong to the tour.
   *
   * ORDER-INSENSITIVE vs. the repush that carries the row (design §2.5). Everything here is keyed
   * on the NAME, which Unity has already made unique, and the scroll is a LATCH rather than an
   * attempt: the repush is async and this message routinely wins the race.
   *
   * The name is uppercased and trimmed because that is the key the Cast list is built on
   * (BrowseMode's `rosterByKey`) and what `screenplayExpandedCharacter` holds. Unity sends the
   * final UPPERCASE name, so this normally changes nothing — it is what keeps the card openable if
   * that ever stops being true.
   */
  castMemberAdded: (msg: CastMemberAddedMessage) => {
    const rawName = typeof msg.payload?.name === 'string' ? msg.payload.name.trim() : '';
    const name = rawName === '' ? null : rawName.toUpperCase();
    // `characterId` is the finale's SHORTCUT to the new row (Edit appearance); 0 / negative /
    // absent all mean "no id yet", and the name lookup is the fallback — so they normalise to the
    // same null rather than to a number no cast row carries.
    const rawId = Number(msg.payload?.characterId);
    const characterId = Number.isFinite(rawId) && rawId > 0 ? rawId : null;

    const ui = useUiStore.getState();
    ui.setScreenplayBrowseTab('characters');
    // A nameless cast member cannot key a card, so there is nothing to open or reveal — but the
    // tour below still has to reach its finale rather than dead-end on panel 3.
    if (name !== null) {
      ui.setScreenplayExpandedCharacter(name);
      ui.setScreenplayRevealCharacter(name);
      // …and the LIST has to be able to show it. Both of the Cast tab's filters survive the
      // create-session repush (actions.ts keeps them on a preserving load), and either one alone
      // makes the landing above a no-op the user reads as "where did my character go?": a search
      // query the new name does not match filters the card out (BrowseMode's filteredCharacters),
      // and a scene filter drops the persisted roster entirely in favour of dialogue-derived rows
      // — which a brand-new cast member with no lines can never be one of. Clearing them is the
      // rest of the same job, and it also lets the reveal latch above be spent instead of sitting
      // armed until the next fresh open.
      ui.setScreenplaySearchQuery('');
      ui.setScreenplayFilterSceneId(null);
    }

    if (!selectCharactersTourActive(useTutorialStore.getState())) return;
    ui.setWorkspaceRoute('screenplay');
    useTutorialStore.getState().enterCharactersFinale({ name: name ?? '', characterId });
  },
} satisfies HandlerMap;
